import { useEffect } from 'react'

type DragAutoScrollOptions = {
  /** Kenardan kaç px içeride kaydırma başlasın. */
  edgeSize?: number
  /** Kenara tam yapışıldığında kare başına kaydırma miktarı (px). */
  maxSpeed?: number
}

/**
 * Native HTML5 sürükle-bırak (`draggable` + `onDragStart`/`onDrop`) sırasında imleç
 * ekranın üst veya alt kenarına yaklaştığında sayfayı otomatik kaydırır.
 *
 * NEDEN GEREKLİ: Tarayıcının native drag & drop uygulaması sayfayı kendiliğinden
 * kaydırmaz. Bu yüzden ekranda görünenden uzun bir listede bir öğeyi yukarı taşımak
 * mümkün olmuyordu — sürüklerken sayfa yerinde kalıyordu. (react-beautiful-dnd bu işi
 * kendi yapıyor; sorun yalnızca native DnD kullanan ekranlarda.)
 *
 * `dragover` yalnızca imleç HAREKET ETTİKÇE tetiklendiği için kaydırma doğrudan o olaya
 * bağlanamaz: kullanıcı kenarda durduğunda kaydırma da duruverirdi. Bunun yerine son
 * imleç konumu kaydedilip requestAnimationFrame döngüsüyle kaydırılıyor.
 */
export function useDragAutoScroll({ edgeSize = 120, maxSpeed = 22 }: DragAutoScrollOptions = {}) {
  useEffect(() => {
    let frame = 0
    let pointerY: number | null = null
    let pointerTarget: Element | null = null

    /** İmlecin üzerinde bulunduğu elemandan yukarı çıkarak gerçekten kaydırılabilen kabı bulur. */
    const scrollerOf = (element: Element | null): Element | Window => {
      let node: Element | null = element

      while (node && node !== document.body && node !== document.documentElement) {
        const overflowY = window.getComputedStyle(node).overflowY
        const canScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay'

        if (canScroll && node.scrollHeight > node.clientHeight) return node

        node = node.parentElement
      }

      return window
    }

    const step = () => {
      frame = requestAnimationFrame(step)

      if (pointerY === null) return

      const viewportHeight = window.innerHeight
      let delta = 0

      if (pointerY < edgeSize) {
        // Kenara yaklaştıkça hızlanır: tam kenarda maxSpeed, edgeSize sınırında ~0.
        const ratio = Math.min(1, (edgeSize - pointerY) / edgeSize)
        delta = -Math.ceil(ratio * maxSpeed)
      } else if (pointerY > viewportHeight - edgeSize) {
        const ratio = Math.min(1, (pointerY - (viewportHeight - edgeSize)) / edgeSize)
        delta = Math.ceil(ratio * maxSpeed)
      }

      if (delta === 0) return

      const scroller = scrollerOf(pointerTarget)
      if (scroller === window) window.scrollBy(0, delta)
      else (scroller as Element).scrollTop += delta
    }

    const start = () => {
      if (frame === 0) frame = requestAnimationFrame(step)
    }

    const stop = () => {
      if (frame !== 0) {
        cancelAnimationFrame(frame)
        frame = 0
      }
      pointerY = null
      pointerTarget = null
    }

    // preventDefault ÇAĞRILMIYOR: burada yapılırsa tüm doküman geçerli bir bırakma
    // hedefine dönüşür ve sayfanın kendi bırakma mantığı bozulur. Yalnızca konum okunuyor.
    const onDragOver = (event: DragEvent) => {
      pointerY = event.clientY
      pointerTarget = event.target as Element | null
    }

    // İmleç pencereden tamamen çıktıysa kaydırmayı durdur; aksi halde son konumda
    // takılı kalıp sonsuza kadar kaydırmaya devam ederdi.
    const onDragLeave = (event: DragEvent) => {
      if (event.relatedTarget === null) pointerY = null
    }

    // CAPTURE fazı zorunlu, bubble ÇALIŞMAZ. Sürüklenen öğelerin kendi onDragStart
    // handler'ları e.stopPropagation() çağırıyor; React 18 dinleyicilerini kök kapsayıcıya
    // bağladığı için olay oradan document'a hiç ulaşmıyor ve döngü hiç başlamıyordu.
    // Capture fazı document'tan hedefe doğru indiği için stopPropagation'dan önce çalışır.
    const capture = { capture: true } as const

    document.addEventListener('dragstart', start, capture)
    document.addEventListener('dragover', onDragOver, capture)
    document.addEventListener('dragleave', onDragLeave, capture)
    document.addEventListener('dragend', stop, capture)
    document.addEventListener('drop', stop, capture)

    return () => {
      stop()
      document.removeEventListener('dragstart', start, capture)
      document.removeEventListener('dragover', onDragOver, capture)
      document.removeEventListener('dragleave', onDragLeave, capture)
      document.removeEventListener('dragend', stop, capture)
      document.removeEventListener('drop', stop, capture)
    }
  }, [edgeSize, maxSpeed])
}
